import { useState, useEffect, useCallback } from 'react';
import { coreApi } from '@shared/api/client';
import { ConnectorItem } from './types';

const INITIAL_FALLBACK_CONNECTORS: ConnectorItem[] = [
  {
    id: 'conn-1',
    name: 'Slack Workspace',
    provider: 'slack',
    status: 'connected',
    credentials_masked: 'xoxb-••••••••9812',
    triggers_count: 4,
    actions_count: 6,
    last_health_check: new Date(Date.now() - 5 * 60000).toISOString(),
    created_at: new Date(Date.now() - 14 * 86400000).toISOString(),
  },
  {
    id: 'conn-2',
    name: 'Stripe Payments API',
    provider: 'stripe',
    status: 'connected',
    credentials_masked: 'sk_live_••••••••1104',
    triggers_count: 8,
    actions_count: 12,
    last_health_check: new Date(Date.now() - 12 * 60000).toISOString(),
    created_at: new Date(Date.now() - 30 * 86400000).toISOString(),
  },
  {
    id: 'conn-3',
    name: 'GitHub Repository Webhooks',
    provider: 'github',
    status: 'connected',
    credentials_masked: 'ghp_••••••••33df',
    triggers_count: 5,
    actions_count: 4,
    last_health_check: new Date(Date.now() - 60 * 60000).toISOString(),
    created_at: new Date(Date.now() - 40 * 86400000).toISOString(),
  },
];

export function useConnectors() {
  const [connectors, setConnectors] = useState<ConnectorItem[]>(INITIAL_FALLBACK_CONNECTORS);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConnectors = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await coreApi.get<ConnectorItem[]>('/api/connectors');
      if (Array.isArray(data) && data.length > 0) {
        setConnectors(data);
      } else {
        setConnectors(INITIAL_FALLBACK_CONNECTORS);
      }
    } catch {
      setConnectors(INITIAL_FALLBACK_CONNECTORS);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConnectors();
  }, [fetchConnectors]);

  const createConnector = async (input: { name: string; provider: string }) => {
    try {
      const res = await coreApi.post<ConnectorItem>('/api/connectors', input);
      if (res) {
        setConnectors((prev) => [res, ...prev]);
        return res;
      }
    } catch {
      // fallback
    }
    const mockConn: ConnectorItem = {
      id: `conn-${Date.now()}`,
      name: input.name,
      provider: input.provider as any,
      status: 'connected',
      credentials_masked: 'sec_••••••••8120',
      triggers_count: 2,
      actions_count: 4,
      last_health_check: new Date().toISOString(),
      created_at: new Date().toISOString(),
    };
    setConnectors((prev) => [mockConn, ...prev]);
    return mockConn;
  };

  const testConnector = async (id: string) => {
    try {
      await coreApi.post(`/api/connectors/${id}/test`, {});
    } catch {
      // fallback
    }
    setConnectors((prev) =>
      prev.map((c) => (c.id === id ? { ...c, last_health_check: new Date().toISOString(), status: 'connected' as const } : c))
    );
  };

  return {
    connectors,
    isLoading,
    createConnector,
    testConnector,
    refresh: fetchConnectors,
  };
}

export default useConnectors;
